import React, { useState, useRef, useEffect } from 'react';
import { useUserAssets, useUserAssetStats, useUpdateUserAsset } from '../hooks/useUserAssets';
import { UserAsset, UserAssetFilters } from '../types';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { userAssetService } from '../services/userAssetService';
import { getAssetTypeLabel, getFilterableAssetTypes } from '../constants';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ReactivateAssetDialog, {
  isAssetExpired,
  reactivateErrorMessage,
  REACTIVATE_REASON_MAX_LENGTH,
} from '../components/ReactivateAssetDialog';

const UserAssetListPage: React.FC = () => {
  const [filters, setFilters] = useState<UserAssetFilters>({
    page: 1,
    limit: 20,
  });
  const [searchQuery, setSearchQuery] = useState('');
  // Giữ cả bản ghi (không chỉ id) để phân loại được theo trạng thái, kể cả dòng đã chọn ở trang khác.
  const [selectedAssets, setSelectedAssets] = useState<Map<string, UserAsset>>(new Map());
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  const [isBulkReactivating, setIsBulkReactivating] = useState(false);
  // Cờ mở/đóng tách khỏi dữ liệu hiển thị: khi đóng chỉ tắt cờ, dữ liệu giữ nguyên đến lần mở sau,
  // để nội dung Dialog không đổi (thành "0 tài sản"/rỗng) trong lúc animation đóng.
  const [bulkReactivateIds, setBulkReactivateIds] = useState<string[]>([]);
  const [bulkReactivateReason, setBulkReactivateReason] = useState('');
  const [isBulkReactivateOpen, setIsBulkReactivateOpen] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState<UserAsset | null>(null);
  const [isReactivateDialogOpen, setIsReactivateDialogOpen] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useUserAssets(filters);
  const { data: stats } = useUserAssetStats();
  const updateMutation = useUpdateUserAsset(); // sửa max_download
  const queryClient = useQueryClient();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, search: searchQuery, page: 1 }));
    setSelectedAssets(new Map()); // Clear selection when searching
  };

  const handleFilterChange = (key: keyof UserAssetFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    setSelectedAssets(new Map()); // Clear selection when filtering
  };

  // Mọi dòng đều chọn được; hành động nào áp cho dòng nào do canRevoke/canReactivate quyết định.
  const handleSelectAll = (checked: boolean) => {
    const newSelected = new Map(selectedAssets);
    data?.data?.forEach(asset => {
      if (checked) {
        newSelected.set(asset.id, asset);
      } else {
        newSelected.delete(asset.id);
      }
    });
    setSelectedAssets(newSelected);
  };

  const handleSelectAsset = (asset: UserAsset, checked: boolean) => {
    const newSelected = new Map(selectedAssets);
    if (checked) {
      newSelected.set(asset.id, asset);
    } else {
      newSelected.delete(asset.id);
    }
    setSelectedAssets(newSelected);
  };

  // Luật lấy từ logic hiện có:
  // - Thu hồi: API chỉ từ chối tài sản đã `revoked` → mọi trạng thái khác đều thu hồi được.
  // - Kích hoạt lại: API chỉ nhận `revoked` và từ chối nếu `expires_at` đã qua.
  const canRevoke = (asset: UserAsset) => asset.status !== 'revoked';
  const canReactivate = (asset: UserAsset) => asset.status === 'revoked' && !isAssetExpired(asset);

  const selectedList = Array.from(selectedAssets.values());
  const revocableIds = selectedList.filter(canRevoke).map(asset => asset.id);
  const reactivatableIds = selectedList.filter(canReactivate).map(asset => asset.id);

  const handleRevokeSelected = async () => {
    if (revocableIds.length === 0) return;

    setIsRevoking(true);
    try {
      const assetIds = revocableIds;
      // revokeMultiple nuốt lỗi từng dòng và chỉ trả về các dòng thành công.
      const revoked = await userAssetService.revokeMultiple(assetIds, revokeReason || undefined);
      const failedCount = assetIds.length - revoked.length;

      // Clear selection and close modal
      setSelectedAssets(new Map());
      setShowRevokeModal(false);
      setRevokeReason('');

      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['userAssetStats'] });

      if (revoked.length === 0) {
        toast.error('Không thu hồi được tài sản nào. Vui lòng thử lại.');
      } else if (failedCount > 0) {
        toast.error(`Đã thu hồi ${revoked.length} tài sản. ${failedCount} tài sản không thu hồi được.`);
      } else {
        toast.success(`Đã thu hồi thành công ${revoked.length} tài sản người dùng.`);
      }
    } catch (error) {
      console.error('Failed to revoke assets:', error);
      toast.error('Có lỗi xảy ra khi thu hồi tài sản. Vui lòng thử lại.');
    } finally {
      setIsRevoking(false);
    }
  };

  // Mở Dialog dùng chung: còn hạn → xác nhận + lý do; đã hết hạn → gia hạn trước (BE từ chối tài sản hết hạn).
  const handleReactivate = (asset: UserAsset) => {
    setReactivateTarget(asset);
    setIsReactivateDialogOpen(true);
  };

  const handleReactivateSelected = () => {
    if (reactivatableIds.length === 0) return;
    setBulkReactivateIds(reactivatableIds);
    setBulkReactivateReason('');
    setIsBulkReactivateOpen(true);
  };

  const confirmBulkReactivate = async () => {
    if (isBulkReactivating) return;
    setIsBulkReactivating(true);
    try {
      const { succeeded, failed } = await userAssetService.reactivateMultiple(
        bulkReactivateIds,
        bulkReactivateReason.trim() || undefined
      );
      setSelectedAssets(new Map());
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['userAssetStats'] });

      if (failed.length === 0) {
        toast.success(`Đã kích hoạt lại thành công ${succeeded.length} tài sản người dùng.`);
        return;
      }

      console.warn('Some reactivate operations failed:', failed);
      // 401: interceptor đã chuyển về trang đăng nhập, không báo lỗi kích hoạt.
      const relevantFailures = failed.filter(f => f.status !== 401);
      if (relevantFailures.length === 0) return;

      // Gom lý do: "Tài sản đã quá hạn… (2); Tài sản không tồn tại… (1)"
      const reasonCounts = new Map<string, number>();
      relevantFailures.forEach(f => {
        const reason = reactivateErrorMessage(f.message, f.status);
        reasonCounts.set(reason, (reasonCounts.get(reason) || 0) + 1);
      });
      const reasons = Array.from(reasonCounts, ([reason, count]) => `${reason} (${count})`).join('; ');

      toast.error(
        succeeded.length > 0
          ? `Đã kích hoạt lại ${succeeded.length} tài sản, ${failed.length} tài sản thất bại. Lý do: ${reasons}`
          : `Không kích hoạt lại được ${failed.length} tài sản. Lý do: ${reasons}`,
        { duration: 6000 }
      );
    } catch (error) {
      console.error('Failed to reactivate assets:', error);
      toast.error('Có lỗi xảy ra khi kích hoạt lại tài sản. Vui lòng thử lại.');
    } finally {
      setIsBulkReactivating(false);
      setIsBulkReactivateOpen(false);
    }
  };

  // Dòng bị thu hồi làm mờ từng ô thay vì cả <tr>, để nút "Kích hoạt lại" không bị mờ theo.
  const dimmed = (asset: UserAsset) => (asset.status === 'revoked' ? 'opacity-60' : '');

  const pageAssets = data?.data ?? [];
  const selectedOnPage = pageAssets.filter(asset => selectedAssets.has(asset.id)).length;
  const isAllSelected = pageAssets.length > 0 && selectedOnPage === pageAssets.length;
  const isIndeterminate = selectedOnPage > 0 && !isAllSelected;

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Hoạt động' },
      expired: { color: 'bg-yellow-100 text-yellow-800', label: 'Hết hạn' },
      revoked: { color: 'bg-red-100 text-red-800', label: 'Bị thu hồi' },
      suspended: { color: 'bg-gray-100 text-gray-800', label: 'Tạm dừng' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const handleDoubleClickMaxDownloads = (asset: UserAsset) => {
    // If editing another asset, cancel current edit first
    if (editingAssetId && editingAssetId !== asset.id) {
      handleCancelEdit();
    }
    setEditingAssetId(asset.id);
    setEditingValue((asset.max_download ?? 2).toString());
  };

  const handleSaveMaxDownloads = async (assetId: string) => {
    const newValue = parseInt(editingValue, 10);
    if (isNaN(newValue) || newValue < 0) {
      toast.error('Vui lòng nhập số hợp lệ (>= 0)');
      setEditingAssetId(null);
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: assetId,
        data: { max_download: newValue },
      });
      setEditingAssetId(null);
      setEditingValue('');
    } catch (error) {
      console.error('Failed to update max_download:', error);
      toast.error('Có lỗi xảy ra khi cập nhật số thiết bị tối đa. Vui lòng thử lại.');
    }
  };

  const handleCancelEdit = () => {
    setEditingAssetId(null);
    setEditingValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, assetId: string) => {
    if (e.key === 'Enter') {
      handleSaveMaxDownloads(assetId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (editingAssetId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingAssetId]);


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
        <p className="text-gray-500">Không thể tải danh sách tài sản người dùng.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tìm kiếm
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm theo tên, email, mã kích hoạt..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trạng thái
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tất cả</option>
                <option value="active">Hoạt động</option>
                <option value="expired">Hết hạn</option>
                <option value="revoked">Bị thu hồi</option>
                <option value="suspended">Tạm dừng</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loại tài sản
              </label>
              <select
                value={filters.asset_type || ''}
                onChange={(e) => handleFilterChange('asset_type', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tất cả</option>
                {getFilterableAssetTypes(stats?.by_type, filters.asset_type).map((type) => (
                  <option key={type} value={type}>{getAssetTypeLabel(type)}</option>
                ))}
              </select>
            </div>

          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Tìm kiếm
            </button>
          </div>
        </form>
      </div>

      {/* Results */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Danh sách Tài sản ({data?.total || 0})
            </h3>
            <div className="flex space-x-2">
              {revocableIds.length > 0 && (
                <button
                  onClick={() => setShowRevokeModal(true)}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Thu hồi ({revocableIds.length})
                </button>
              )}
              {reactivatableIds.length > 0 && (
                <button
                  onClick={handleReactivateSelected}
                  disabled={isBulkReactivating}
                  className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {isBulkReactivating ? 'Đang kích hoạt...' : `Kích hoạt lại (${reactivatableIds.length})`}
                </button>
              )}
              <button className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                Xuất Excel
              </button>
              <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                Tạo mới
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Người dùng
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tài sản
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loại
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gán lúc
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hết hạn
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lượt tải (lịch sử)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Số thiết bị tối đa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thiết bị
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data?.map((asset: UserAsset) => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)}`}>
                    <input
                      type="checkbox"
                      checked={selectedAssets.has(asset.id)}
                      onChange={(e) => handleSelectAsset(asset, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)}`}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {asset.user?.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {asset.user?.name || 'N/A'}
                        </div>
                        {(asset.user?.user_name || asset.user?.code) && (
                          <div className="text-xs text-gray-500">
                            @{asset.user?.user_name || asset.user?.code}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)}`}>
                    <div className="flex items-center">
                      {asset.book?.cover_image_url && (
                        <img
                          className="h-10 w-10 rounded object-cover"
                          src={asset.book.cover_image_url}
                          alt={asset.book.title}
                        />
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {asset.book?.title || 'N/A'}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {asset.activation_code?.code && (
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {asset.activation_code.code}
                            </span>
                          )}
                          {asset.metadata?.activation_code && (
                            <span className="font-mono text-xs bg-blue-100 px-2 py-1 rounded">
                              {asset.metadata.activation_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)} text-sm text-gray-900`}>
                    {getAssetTypeLabel(asset.asset_type)}
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className="flex flex-col items-start gap-1">
                      {getStatusBadge(asset.status)}
                      {asset.status === 'revoked' && (
                        <button
                          type="button"
                          onClick={() => handleReactivate(asset)}
                          title={isAssetExpired(asset) ? 'Tài sản đã quá hạn, cần gia hạn trước khi kích hoạt lại' : undefined}
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Kích hoạt lại
                        </button>
                      )}
                    </div>
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)} text-sm text-gray-500`}>
                    {format(new Date(asset.assigned_at), 'dd/MM/yyyy HH:mm')}
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)} text-sm text-gray-500`}>
                    {asset.expires_at 
                      ? format(new Date(asset.expires_at), 'dd/MM/yyyy HH:mm')
                      : 'Không giới hạn'
                    }
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)} text-sm text-gray-500`}>
                    {/* Chỉ số lượt, không đối chiếu hạn mức: hạn mức đếm theo
                        thiết bị nên một máy tải lại nhiều lần vẫn chỉ một suất. */}
                    <div className="flex items-center">
                      <span className="text-green-600 font-medium">
                        {asset.download_count || 0}
                      </span>
                    </div>
                  </td>
                  <td
                    className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)} text-sm text-gray-500`}
                    onDoubleClick={() => handleDoubleClickMaxDownloads(asset)}
                  >
                    {editingAssetId === asset.id ? (
                      <input
                        ref={inputRef}
                        type="number"
                        min="0"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={() => handleSaveMaxDownloads(asset.id)}
                        onKeyDown={(e) => handleKeyDown(e, asset.id)}
                        className="w-20 px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={updateMutation.isPending}
                      />
                    ) : (
                      <span 
                        className="text-gray-900 font-medium cursor-pointer hover:text-blue-600 transition-colors"
                        title="Double-click để chỉnh sửa"
                      >
                        {asset.max_download ?? 2}
                      </span>
                    )}
                  </td>
                  <td className={`px-6 py-2 whitespace-nowrap ${dimmed(asset)} text-sm text-gray-500`}>
                    <div className="flex items-center">
                      <span className="text-blue-600 font-medium">
                        {asset.device_count || 0}
                      </span>
                      {(asset.max_devices ?? asset.max_download) && (
                        <span className="text-gray-400 mx-1">/</span>
                      )}
                      {(asset.max_devices ?? asset.max_download) && (
                        <span className="text-gray-500">
                          {asset.max_devices ?? asset.max_download}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                disabled={filters.page === 1}
                onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trước
              </button>
              <button
                disabled={!data || data.data.length < (filters.limit || 20)}
                onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sau
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Hiển thị{' '}
                  <span className="font-medium">
                    {((filters.page || 1) - 1) * (filters.limit || 20) + 1}
                  </span>{' '}
                  đến{' '}
                  <span className="font-medium">
                    {Math.min((filters.page || 1) * (filters.limit || 20), data.total)}
                  </span>{' '}
                  trong tổng số{' '}
                  <span className="font-medium">{data.total}</span>{' '}
                  kết quả
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    disabled={filters.page === 1}
                    onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Trước
                  </button>
                  <button
                    disabled={!data || data.data.length < (filters.limit || 20)}
                    onClick={() => setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sau
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Xác nhận thu hồi tài sản
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn thu hồi <strong>{revocableIds.length}</strong> tài sản người dùng đã chọn?
                  Có thể kích hoạt lại sau ở cột Trạng thái.
                </p>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lý do thu hồi (tùy chọn)
                </label>
                <textarea
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Nhập lý do thu hồi..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
              <div className="items-center px-4 py-3 mt-4">
                <button
                  onClick={handleRevokeSelected}
                  disabled={isRevoking}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRevoking ? 'Đang thu hồi...' : 'Xác nhận thu hồi'}
                </button>
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    setRevokeReason('');
                  }}
                  disabled={isRevoking}
                  className="mt-3 px-4 py-2 bg-white text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reactivate Confirmation Modal */}
      <Dialog
        open={isBulkReactivateOpen}
        onOpenChange={(open) => {
          // Không cho đóng khi request đang chạy.
          if (!open && !isBulkReactivating) setIsBulkReactivateOpen(false);
        }}
      >
        {/* Cùng bố cục popup "Xác nhận thu hồi tài sản" (w-96, p-5, căn giữa), màu xanh cho hành động kích hoạt lại */}
        <DialogContent className="max-w-sm gap-0 rounded-md p-5 sm:rounded-md">
          <div className="mt-3 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <RotateCcw className="h-6 w-6 text-blue-600" />
            </div>
            <DialogHeader className="space-y-0 text-center sm:text-center">
              <DialogTitle className="mt-4 text-lg font-medium leading-normal tracking-normal text-gray-900">
                Xác nhận kích hoạt lại
              </DialogTitle>
              <DialogDescription className="mt-2 px-7 py-3 text-sm text-gray-500">
                Kích hoạt lại <strong>{bulkReactivateIds.length}</strong> tài sản đang bị thu hồi?
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <label htmlFor="bulk-reactivate-reason" className="block text-sm font-medium text-gray-700 mb-2">
                Lý do kích hoạt lại (tùy chọn)
              </label>
              <Textarea
                id="bulk-reactivate-reason"
                value={bulkReactivateReason}
                onChange={(e) => setBulkReactivateReason(e.target.value)}
                maxLength={REACTIVATE_REASON_MAX_LENGTH}
                placeholder="Nhập lý do kích hoạt lại..."
                rows={3}
                disabled={isBulkReactivating}
                className="min-h-0 border-gray-300 text-base focus-visible:ring-blue-500 focus-visible:ring-offset-0"
              />
            </div>
            <div className="px-4 py-3 mt-4">
              <Button
                variant="primary"
                onClick={confirmBulkReactivate}
                isLoading={isBulkReactivating}
                disabled={isBulkReactivating}
                className="w-full text-base shadow-sm"
              >
                {isBulkReactivating ? 'Đang kích hoạt...' : 'Xác nhận kích hoạt lại'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsBulkReactivateOpen(false)}
                disabled={isBulkReactivating}
                className="mt-3 w-full text-base text-gray-700 shadow-sm"
              >
                Hủy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Kích hoạt lại 1 tài sản: xác nhận + lý do, hoặc gia hạn trước nếu đã hết hạn */}
      <ReactivateAssetDialog
        asset={reactivateTarget}
        open={isReactivateDialogOpen}
        onOpenChange={setIsReactivateDialogOpen}
        onReactivated={(asset) => {
          // Nếu dòng này đang được chọn, cập nhật trạng thái để toolbar đếm lại đúng nhóm.
          if (selectedAssets.has(asset.id)) {
            setSelectedAssets(prev => new Map(prev).set(asset.id, asset));
          }
        }}
      />
    </div>
  );
};

export default UserAssetListPage;
export { UserAssetListPage };
